import { useEffect, useId, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { Badge, type BadgeVariant } from './Badge.js';
import { Icon } from './Icon.js';
import { Kbd } from './Kbd.js';
import { VisuallyHidden } from './VisuallyHidden.js';
import './SearchField.css';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Placeholder AND, unless `label` is given, the field's accessible name. */
  placeholder?: string;
  /**
   * Accessible name. Defaults to the placeholder, because a search input with a
   * placeholder and no label is the single commonest unlabelled-control failure —
   * a placeholder disappears the moment someone types, so it cannot be the only
   * label a screen reader has.
   */
  label?: string;
  /** Leading glyph. Decorative — the accessible name carries the meaning. */
  icon?: ReactNode;
  /**
   * Show the `/` shortcut hint and bind the key. The prototype advertises it in
   * the field; binding without showing it would make it undiscoverable, and
   * showing it without binding would be a lie.
   */
  shortcut?: boolean;
  onSubmit?: (value: string) => void;
  /**
   * Results for the current `value`, rendered in the prototype's dropdown
   * (kind tag · label · mono meta). The FIELD owns only the popover chrome;
   * what is searchable, and how, belongs to the app — F29's index question.
   * Omit entirely (vs. an empty array) to render no popover at all.
   */
  results?: SearchResult[];
}

export interface SearchResult {
  id: string;
  /** The kind tag — the prototype colour-codes finding/pair/action/run/source/record. */
  kind: string;
  /** Tag tint. Maps kinds onto the semantic families rather than per-kind hexes. */
  tone?: BadgeVariant;
  label: string;
  /** Right-aligned mono metadata — an id, a system name. */
  meta?: string;
  onSelect?: () => void;
}

/**
 * Global search (design-system catalogue, Tier 2) — the topbar's search pill.
 *
 * Added by the design-fidelity audit (S-3): the prototype's topbar is built
 * around this element and the catalogue had only `CommandPalette` as Planned, so
 * both apps shipped a topbar with no search at all.
 *
 * This is the input surface only. What it searches, and the result popover, are
 * F29 backlog — deliberately not faked here, because a search box that accepts
 * text and does nothing is worse than one that is visibly not built yet.
 */
export function SearchField({
  value,
  onChange,
  placeholder = 'Search',
  label,
  icon,
  shortcut = false,
  onSubmit,
  results,
}: SearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();

  useEffect(() => {
    if (!shortcut) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/') return;
      const target = event.target as HTMLElement | null;
      // Do not steal the key from someone typing a slash into another field, or
      // from a contenteditable surface.
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcut]);

  const [focused, setFocused] = useState(false);
  const showResults = results != null && focused && value.trim().length > 0;

  return (
    <div
      className="dpr-search__wrap"
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        // Keep the popover while focus moves INTO it — losing it on the first
        // Tab press would make the results mouse-only.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false);
      }}
    >
      <div className="dpr-search">
      <span className="dpr-search__icon" aria-hidden="true">
        {icon ?? <Icon name="search" size={18} />}
      </span>
      <input
        ref={inputRef}
        type="search"
        className="dpr-search__input"
        value={value}
        placeholder={placeholder}
        aria-label={label ?? placeholder}
        aria-describedby={shortcut ? hintId : undefined}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSubmit) onSubmit(value);
        }}
      />
        {shortcut ? (
        <span className="dpr-search__hint">
          {/* The visible Kbd is aria-hidden; the description below is what a
              screen-reader user actually gets, in words rather than as a
              punctuation character read out of context. */}
          <span aria-hidden="true">
            <Kbd>/</Kbd>
          </span>
          <VisuallyHidden>
            <span id={hintId}>Press slash to focus search</span>
          </VisuallyHidden>
        </span>
      ) : null}
      </div>

      {showResults ? (
        <div className="dpr-search__results" role="listbox" aria-label="Search results">
          {results.length === 0 ? (
            <p className="dpr-search__empty">Nothing matches “{value.trim()}”.</p>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                role="option"
                aria-selected={false}
                className="dpr-search__result"
                // pointerdown, not click: the input's blur fires between
                // mousedown and click and would unmount the row mid-press.
                onPointerDown={(e) => {
                  e.preventDefault();
                  r.onSelect?.();
                  setFocused(false);
                }}
              >
                <Badge variant={r.tone ?? 'neutral'}>{r.kind}</Badge>
                <span className="dpr-search__result-label">{r.label}</span>
                {r.meta ? <span className="dpr-search__result-meta">{r.meta}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
